import { Link } from "react-router-dom";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";
import { ShieldCrossIcon, CrosshairIcon, IchthysIcon } from "@/components/Icons";
import { BookOpen } from "lucide-react";

const HERITAGE_IMG = "https://images.unsplash.com/photo-1674953477961-d46dd2dfd58f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwY3Jvc3MlMjBsZWF0aGVyJTIwam91cm5hbCUyMGNvZmZlZSUyMGJvb2slMjByYW5nZSUyMGdlYXJ8ZW58MHx8fHwxNzkxMjI3MDA5fDA&ixlib=rb-4.1.0&q=85";
const RANGE_IMG = "https://images.unsplash.com/photo-1533091917107-98f819e4cb54?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxzaG9vdGluZyUyMHJhbmdlJTIwb3V0ZG9vciUyMHJpZmxlJTIwdHJhaW5pbmclMjBncm91cCUyMHNxdWFkfGVufDB8fHx8MTc5MTIyNzAxNnww&ixlib=rb-4.1.0&q=85";
const JOURNAL_IMG = "https://images.unsplash.com/photo-1679891984409-0a3b1c4984ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwY3Jvc3MlMjBsZWF0aGVyJTIwam91cm5hbCUyMGNvZmZlZSUyMGJvb2slMjByYW5nZSUyMGdlYXJ8ZW58MHx8fHwxNzkxMjI3MDA5fDA&ixlib=rb-4.1.0&q=85";

const LEADERSHIP = [
  { name: "Marcus Whitfield", role: "Chaplain & President", bio: "Former Army chaplain, 14 years. NRA-certified pistol instructor. Leads the order's teaching and pastoral care." },
  { name: "David Okafor", role: "Director of Training", bio: "Precision rifle competitor and certified long-range instructor. Builds every course in the club curriculum." },
  { name: "Sarah Lindqvist", role: "Safety Officer", bio: "Defensive pistol instructor and trauma nurse. Owns the club's safety standards and first-aid readiness." },
  { name: "Ruth Abimbola", role: "Quartermaster", bio: "Runs the armory store, benevolence fund, and logistics for every muster and outreach event." },
];

const VALUES = [
  { icon: BookOpen, title: "Scripture First", text: "The Word governs the trigger finger. Every course and gathering begins and ends in prayer and study." },
  { icon: ShieldCrossIcon, title: "Duty to Protect", text: "We train because the protection of the innocent is a sacred charge, not a hobby." },
  { icon: CrosshairIcon, title: "Excellence in Craft", text: "Sloppiness dishonors the calling. We pursue mastery with humility and patience." },
  { icon: IchthysIcon, title: "Fellowship & Service", text: "No member trains alone. We carry each other's burdens on and off the range." },
];

const CODE = [
  "Treat every firearm as loaded — always, without exception.",
  "Never point at anything you are not willing to destroy.",
  "Keep your finger off the trigger until your sights are on target.",
  "Know your target and what is beyond it.",
  "No member shoots under the influence of alcohol, ever.",
  "A new shooter is never left unsupervised on the line.",
  "We submit to lawful authority and range commands immediately.",
];

export default function About() {
  return (
    <main className="pt-16" data-testid="about-page">
      <section className="relative overflow-hidden noise-overlay crosshair-grid border-b border-gold/15">
        <img src={HERITAGE_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 to-obsidian" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-28">
          <p className="eyebrow mb-5">About the Order</p>
          <MaskedLines
            lines={["SWORD &", "SCRIPTURE."]}
            lineClassName="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight text-parchment"
          />
          <p className="mt-7 max-w-2xl text-stone-300 text-sm sm:text-base leading-relaxed" data-testid="about-mission">
            Crusaders of the Cross exists to unite Christian men and women in the disciplined, lawful, and
            joyful practice of the shooting sports — forming protectors who are as grounded in Scripture
            as they are steady on the firing line.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="heritage-section">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow mb-4">Our Heritage</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wide text-parchment leading-snug">
              Stewardship, not swagger
            </h2>
            <p className="mt-6 text-stone-300 leading-relaxed text-sm sm:text-base">
              Founded in 2026 by a handful of church friends after a Saturday range day turned into a
              Bible study, the order has grown into a chartered club with dedicated range bays, a certified
              instructor corps, and members across four congregations.
            </p>
            <p className="mt-4 text-stone-300 leading-relaxed text-sm sm:text-base">
              We hold that the right to keep and bear arms carries a duty: to train seriously, to store
              safely, and to wield strength only in defense of life and the innocent. Psalm 144:1 hangs
              above our clubhouse door — not as a boast, but as a reminder of who does the training.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 border border-gold/30 pointer-events-none" />
              <img src={RANGE_IMG} alt="Club members training at Iron Valley Outdoor Range" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute bottom-4 left-4 bg-obsidian/85 backdrop-blur px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-gold uppercase">
                Iron Valley — Saturday Muster
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-gold/10 bg-[#0D0F16]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <Reveal>
            <p className="eyebrow mb-4">Statement of Faith &amp; Values</p>
            <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-parchment">What we hold to be true</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="group h-full border border-gold/15 bg-card2/50 p-7 hover:border-gold/40 hover:-translate-y-1 transition-[border-color,transform] duration-300" data-testid={`value-card-${i}`}>
                  <div className="w-14 h-14 border border-gold/30 bg-obsidian/70 flex items-center justify-center mb-5 group-hover:border-gold group-hover:shadow-[0_0_22px_rgba(212,175,55,0.3)] transition-[border-color,box-shadow] duration-300">
                    <v.icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-condensed uppercase tracking-wider text-xl text-parchment">{v.title}</h3>
                  <p className="mt-3 text-sm text-stone-400 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="leadership-section">
        <Reveal>
          <p className="eyebrow mb-4">Leadership</p>
          <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-parchment">Officers of the order</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {LEADERSHIP.map((l, i) => (
            <Reveal key={l.name} delay={i * 0.08}>
              <div className="group relative h-full border border-gold/15 bg-card2/50 p-7 overflow-hidden" data-testid={`leader-card-${i}`}>
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                <LogoMark className="w-8 h-8 text-gold/60 mb-5" />
                <h3 className="font-display text-lg uppercase tracking-wider text-parchment">{l.name}</h3>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold mt-1.5">{l.role}</div>
                <p className="mt-4 text-sm text-stone-400 leading-relaxed">{l.bio}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-gold/10" data-testid="code-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-4">Code of Honor</p>
            <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-parchment leading-snug">Seven rules, zero exceptions</h2>
            <p className="mt-6 text-stone-300 text-sm sm:text-base leading-relaxed">
              Membership is contingent on this code. Violate it on the line and you answer to the safety
              officer and the board. These rules have kept us incident-free since our founding.
            </p>
            <Link to="/bylaws" data-testid="about-bylaws-link" className="inline-block mt-6 font-condensed uppercase tracking-[0.2em] text-sm text-crimson border border-crimson/40 px-5 py-2.5 hover:bg-crimson hover:text-parchment transition-colors duration-300">
              Read the Full Bylaws
            </Link>
            <div className="relative mt-10 hidden lg:block">
              <div className="absolute -inset-3 border border-gold/20 pointer-events-none" />
              <img src={JOURNAL_IMG} alt="Leather training journal and club crest" className="w-full aspect-[4/3] object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7 space-y-px">
            {CODE.map((rule, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex gap-5 items-start border border-gold/15 bg-card2/40 px-6 py-5 hover:bg-card2/80 transition-colors duration-300" data-testid={`code-rule-${i}`}>
                  <span className="font-display text-gold text-xl leading-none mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm sm:text-base text-stone-200 leading-relaxed">{rule}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
